/* eslint-disable max-lines */
// cspell:ignore neue

import {alpha, createTheme} from '@mui/material/styles';

import {snackbarZIndex} from './utils/gaia/zIndices';

export const SPACING_UNIT = 8;
export const AVATAR_WIDTH = 48;
export const BASE_FONT = 14;
const FONT_WEIGHT_BOLD = 600;
// Base font is defined globally in variables.scss. The Map has a different base font of 12 - defined in map.html

export const pxToRem = (px = 14) => {
    return `${px / BASE_FONT}rem`;
};

function variableFontWeight(fontWeight: number) {
    return {
        fontWeight,
        fontVariationSettings: `"wght" ${fontWeight}`,
    };
}

import {
    CONIFER_3,
    GRANITE_2,
    GRANITE_3,
    GRANITE_8,
    GRANITE_9,
    SNOW_BLIND,
    SPRING_4,
    SUMMER_4,
    WINTER_2,
} from './colors';

// 'neutral' is a custom button color variant required by the Gaia Button component.
declare module '@mui/material/Button' {
    interface ButtonPropsColorOverrides {
        neutral: true;
    }
}

declare module '@mui/material/styles' {
    interface PaletteOptions {
        neutral: PaletteOptions['primary'];
    }
}

// Include rules in here only if they're meant to apply website-wide.
// One-off changes should be implemented downstream.
export default createTheme({
    palette: {
        primary: {
            main: CONIFER_3,
        },
        secondary: {
            main: SPRING_4,
        },
        error: {
            main: SUMMER_4,
        },
        text: {
            primary: GRANITE_2,
            secondary: GRANITE_3,
        },
        neutral: {
            main: GRANITE_2,
        },
        divider: GRANITE_8,
    },

    typography: {
        fontSize: BASE_FONT,
        htmlFontSize: BASE_FONT,
        fontFamily: ['Inter', 'Helvetica Neue', 'Helvetica', 'Arial', 'sans-serif'].join(','),
        fontWeightBold: FONT_WEIGHT_BOLD,

        button: {
            textTransform: 'none',
        },
        h1: {
            fontSize: pxToRem(50),
            fontWeight: 'bold',
            fontVariationSettings: `"wght" ${FONT_WEIGHT_BOLD}`,
        },
        h2: {
            fontSize: pxToRem(40),
            fontWeight: 'bold',
            fontVariationSettings: `"wght" ${FONT_WEIGHT_BOLD}`,
        },
        h3: {
            fontSize: pxToRem(32),
            fontWeight: 'bold',
            fontVariationSettings: `"wght" ${FONT_WEIGHT_BOLD}`,
        },
        h4: {
            fontSize: pxToRem(24),
            fontWeight: 'bold',
            fontVariationSettings: `"wght" ${FONT_WEIGHT_BOLD}`,
        },
        h5: {
            fontSize: pxToRem(20),
            fontWeight: 'bold',
            fontVariationSettings: `"wght" ${FONT_WEIGHT_BOLD}`,
        },

        h6: {
            fontSize: pxToRem(18),
            lineHeight: 'normal',
        },
        body1: {
            fontSize: pxToRem(16),
        },
        body2: {
            fontSize: pxToRem(14),
            lineHeight: 1.5,
        },
        subtitle1: {
            fontSize: pxToRem(16),
            fontWeight: 'bold',
            fontVariationSettings: `"wght" ${FONT_WEIGHT_BOLD}`,
            lineHeight: 1.5,
        },
        subtitle2: {
            fontSize: pxToRem(14),
            fontWeight: 'bold',
            fontVariationSettings: `"wght" ${FONT_WEIGHT_BOLD}`,
            lineHeight: 1.5,
        },

        caption: {
            fontSize: pxToRem(12),
            color: GRANITE_3,
        },

        overline: {
            fontSize: pxToRem(12),
        },
    },

    spacing: SPACING_UNIT,

    transitions: {
        duration: {
            enteringScreen: 250,
            leavingScreen: 250,
        },
    },

    components: {
        MuiAccordion: {
            defaultProps: {
                elevation: 0,
            },
        },

        MuiListSubheader: {
            styleOverrides: {
                root: {
                    borderBottomColor: GRANITE_8,
                    borderBottomStyle: 'solid',
                    borderBottomWidth: 1,
                    marginBottom: SPACING_UNIT,
                    paddingBottom: SPACING_UNIT / 2,
                    paddingTop: SPACING_UNIT * 2,
                },

                gutters: {
                    paddingLeft: 0,
                    paddingRight: 0,
                    marginLeft: SPACING_UNIT * 2,
                    marginRight: SPACING_UNIT * 2,
                },

                sticky: {
                    backgroundColor: SNOW_BLIND,
                },
            },
        },

        MuiButtonBase: {
            defaultProps: {
                disableRipple: true,
            },
        },

        MuiButton: {
            styleOverrides: {
                root: {
                    fontSize: 14,
                    ...variableFontWeight(500),
                    '&:active, &:focus, &:hover': {
                        outline: 'none',
                        textDecoration: 'none',
                    },
                },
                // Normalize padding across button sizes and variants
                // This ensures consistency when different button styles
                // (e.g. primary and secondary) display together in the UI
                text: {
                    paddingLeft: SPACING_UNIT * 2,
                    paddingRight: SPACING_UNIT * 2,
                },
                textPrimary: {
                    '&:hover, &:focus,': {
                        color: CONIFER_3,
                    },
                },
                contained: {
                    boxShadow: 'none',
                    paddingLeft: SPACING_UNIT * 2,
                    paddingRight: SPACING_UNIT * 2,
                    '&:hover': {
                        boxShadow: 'none',
                    },
                    '&.Mui-focusVisible': {
                        boxShadow: 'none',
                    },
                },
                containedPrimary: {
                    color: SNOW_BLIND,
                    '&:hover': {
                        color: SNOW_BLIND,
                    },
                    '&.Mui-focusVisible': {
                        color: SNOW_BLIND,
                    },
                },
                sizeLarge: {
                    fontSize: 18,
                    ...variableFontWeight(600),
                    paddingLeft: SPACING_UNIT * 3,
                    paddingRight: SPACING_UNIT * 3,
                },
                textSizeLarge: {
                    fontSize: 18,
                    ...variableFontWeight(600),
                    paddingLeft: SPACING_UNIT * 3,
                    paddingRight: SPACING_UNIT * 3,
                },
                containedSizeLarge: {
                    fontSize: 18,
                    ...variableFontWeight(600),
                    paddingLeft: SPACING_UNIT * 3,
                    paddingRight: SPACING_UNIT * 3,
                },
                outlinedSizeLarge: {
                    fontSize: 18,
                    ...variableFontWeight(600),
                    paddingLeft: SPACING_UNIT * 3,
                    paddingRight: SPACING_UNIT * 3,
                },
                sizeSmall: {
                    fontSize: 12,
                    ...variableFontWeight(400),
                    paddingLeft: SPACING_UNIT,
                    paddingRight: SPACING_UNIT,
                },
                textSizeSmall: {
                    fontSize: 12,
                    ...variableFontWeight(400),
                    paddingLeft: SPACING_UNIT,
                    paddingRight: SPACING_UNIT,
                },
                containedSizeSmall: {
                    fontSize: 12,
                    ...variableFontWeight(400),
                    paddingLeft: SPACING_UNIT,
                    paddingRight: SPACING_UNIT,
                },
                outlinedSizeSmall: {
                    fontSize: 12,
                    ...variableFontWeight(400),
                    paddingLeft: SPACING_UNIT,
                    paddingRight: SPACING_UNIT,
                },
            },
        },

        MuiSnackbar: {
            styleOverrides: {
                root: {
                    zIndex: snackbarZIndex,
                },
            },
        },

        MuiSnackbarContent: {
            styleOverrides: {
                root: {
                    fontSize: 14,
                },
            },
        },

        MuiAvatar: {
            defaultProps: {
                variant: 'rounded',
            },
            styleOverrides: {
                root: {
                    border: `1px solid ${GRANITE_8}`,
                    width: AVATAR_WIDTH,
                    height: AVATAR_WIDTH,
                },
                colorDefault: {
                    backgroundColor: GRANITE_9,
                },
                rounded: {
                    borderRadius: 8,
                },
            },
        },

        MuiDivider: {
            styleOverrides: {
                root: {
                    borderColor: GRANITE_8,
                },
            },
        },

        MuiListItemButton: {
            styleOverrides: {
                root: {
                    '&:hover': {
                        backgroundColor: GRANITE_9,
                    },
                    '&.Mui-selected': {
                        backgroundColor: GRANITE_9,
                        opacity: 1,
                    },
                    '&.Mui-selected:hover, &.Mui-focusVisible, &.Mui-selected.Mui-focusVisible': {
                        backgroundColor: 'rgba(0, 0, 0, 0.08)',
                    },
                },
            },
        },

        MuiListItemIcon: {
            styleOverrides: {
                root: {
                    marginRight: SPACING_UNIT,
                    minWidth: 0,
                },
            },
        },

        MuiToolbar: {
            styleOverrides: {
                root: {
                    width: '100%',
                    maxWidth: 1170,
                    margin: '0 auto',
                },
            },
        },

        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    fontSize: 12,
                    backgroundColor: SNOW_BLIND,
                    color: GRANITE_2,
                    boxShadow:
                        '0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)',
                },
            },
        },

        MuiMenu: {
            defaultProps: {
                anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                },
            },
        },

        MuiMenuItem: {
            styleOverrides: {
                root: {
                    color: GRANITE_2,
                    fontSize: 14,
                    '&:hover, &:focus': {
                        backgroundColor: GRANITE_9,
                        // increase specificity here to prevent inheriting global link styles on django pages
                        color: GRANITE_2,
                    },
                    '&:focus': {
                        outline: 'none',
                    },
                    '&.Mui-selected': {
                        backgroundColor: GRANITE_9,
                    },
                    '&.Mui-selected:hover, &.Mui-focusVisible, &.Mui-selected.Mui-focusVisible': {
                        backgroundColor: 'rgba(0, 0, 0, 0.08)',
                    },
                },
            },
        },

        MuiLink: {
            defaultProps: {
                underline: 'hover',
            },
            styleOverrides: {
                root: {
                    color: WINTER_2,
                    cursor: 'pointer',
                },
                button: {
                    color: CONIFER_3,
                    '&.Mui-focusVisible': {
                        outline: 'none',
                    },
                },
                underlineHover: {
                    '&:focus': {
                        textDecoration: 'underline',
                    },
                },
            },
        },

        MuiCheckbox: {
            defaultProps: {
                color: 'secondary',
            },
            styleOverrides: {
                colorPrimary: {
                    '&:hover': {
                        backgroundColor: alpha(CONIFER_3, 0.04),
                    },
                    '&.Mui-focusVisible': {
                        backgroundColor: alpha(CONIFER_3, 0.08),
                    },
                },
                colorSecondary: {
                    '&:hover': {
                        backgroundColor: GRANITE_9,
                    },
                    '&.Mui-focusVisible': {
                        backgroundColor: GRANITE_8,
                    },
                },
            },
        },

        MuiIconButton: {
            styleOverrides: {
                colorPrimary: {
                    '&.Mui-focusVisible': {
                        backgroundColor: alpha(CONIFER_3, 0.04),
                    },
                },
            },
        },
    },
});
